import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Youtube, Instagram, Rss, Facebook, Plus, X, GripVertical, ExternalLink } from 'lucide-react';

const PLATFORM_OPTIONS = [
  { type: 'youtube',   label: 'YouTube',   icon: <Youtube size={14} className="text-red-500" />,    placeholder: 'youtube.com/channel/UC...' },
  { type: 'instagram', label: 'Instagram', icon: <Instagram size={14} className="text-pink-400" />, placeholder: 'instagram.com/yourhandle' },
  { type: 'facebook',  label: 'Facebook',  icon: <Facebook size={14} className="text-blue-400" />,  placeholder: 'facebook.com/yourpage' },
  { type: 'rss',       label: 'Blog / RSS',icon: <Rss size={14} className="text-bx-gold" />,        placeholder: 'yourblog.com/feed.xml' },
];

export default function CreatorLinksEditor({ bubbleId, initialLinks = [] }) {
  const [links, setLinks] = useState(initialLinks);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ type: 'youtube', url: '', label: '' });

  const save = async (updated) => {
    setBusy(true);
    try {
      await api.patch(`/bubbles/${bubbleId}/links`, { links: updated });
      setLinks(updated);
      toast.success('Creator links saved');
    } catch (_) { toast.error('could not save links'); }
    finally { setBusy(false); }
  };

  const addLink = () => {
    if (!draft.url.trim()) { toast.error('paste a URL first'); return; }
    const updated = [...links, { type: draft.type, url: draft.url.trim(), label: draft.label.trim() }];
    save(updated);
    setDraft({ type: 'youtube', url: '', label: '' });
    setAdding(false);
  };

  const remove = (idx) => save(links.filter((_, i) => i !== idx));

  const platform = (type) => PLATFORM_OPTIONS.find((p) => p.type === type) || PLATFORM_OPTIONS[3];

  return (
    <div className="rounded-2xl border border-white/8 overflow-hidden" style={{ background: 'rgba(10,12,30,0.7)' }}
         data-testid="creator-links-editor">
      <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between">
        <span className="text-sm font-bold text-white">Creator Links</span>
        <button onClick={() => setAdding((a) => !a)} data-testid="add-link-btn"
                className="text-[11px] flex items-center gap-1 text-white/50 hover:text-bx-cyan transition">
          <Plus size={12} /> Add link
        </button>
      </div>

      <AnimatePresence>
        {adding && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-5 py-4 border-b border-white/8 space-y-3 bg-white/[0.02]">
              <div className="flex flex-wrap gap-2">
                {PLATFORM_OPTIONS.map((p) => (
                  <button key={p.type} onClick={() => setDraft((d) => ({ ...d, type: p.type }))}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition ${draft.type === p.type ? 'border-bx-cyan bg-bx-cyan/15 text-white' : 'border-white/15 text-white/50 hover:border-white/30'}`}>
                    {p.icon} {p.label}
                  </button>
                ))}
              </div>
              <input value={draft.url} onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))}
                     placeholder={platform(draft.type).placeholder}
                     data-testid="link-url-input"
                     className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm outline-none focus:border-bx-cyan" />
              <input value={draft.label} onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
                     placeholder="Custom label (optional)"
                     data-testid="link-label-input"
                     className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm outline-none focus:border-bx-cyan" />
              <button onClick={addLink} disabled={busy || !draft.url.trim()}
                      data-testid="link-save-btn"
                      className="px-4 py-1.5 rounded-full bg-bx-cyan text-black text-xs font-bold disabled:opacity-40">
                Add
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="divide-y divide-white/5" data-testid="links-list">
        {links.length === 0 ? (
          <div className="py-8 text-center text-white/40 text-sm">no links yet</div>
        ) : (
          links.map((l, i) => {
            const p = platform(l.type);
            return (
              <div key={i} className="px-5 py-3 flex items-center gap-3" data-testid={`link-item-${i}`}>
                <GripVertical size={14} className="text-white/20 shrink-0" />
                <span className="shrink-0">{p.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white/80 truncate">{l.label || l.url}</p>
                  {l.label && <p className="text-xs text-white/40 truncate">{l.url}</p>}
                </div>
                <a href={l.url} target="_blank" rel="noreferrer" className="text-white/30 hover:text-white/70">
                  <ExternalLink size={12} />
                </a>
                <button onClick={() => remove(i)} data-testid={`link-remove-${i}`}
                        className="text-white/30 hover:text-red-400 transition">
                  <X size={14} />
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
